import React, { useEffect, useLayoutEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { KEY_PRESS } from 'components/spreadsheet/constants/const'
import { Arrow, Cross } from 'components/icons'
import { useBlocker, useBeforeUnload } from 'react-router-dom'
import Button from 'components/button'
import { tv } from 'tailwind-variants'

const modalStyles = tv({
  slots: {
    wrapper: 'flex justify-center h-full w-full overflow-x-hidden overflow-y-auto p-10 max-lg:p-2.5',
    content: 'relative bg-surface flex flex-col rounded-lg shadow p-0 my-auto mx-0'
  },
  variants: {
    width: {
      small: { content: 'w-[35%] max-w-[calc(100%_-_80px)] max-lg:w-full max-lg:max-w-full' },
      medium: { content: 'w-3/4 max-w-[calc(100%_-_80px)] max-lg:w-full max-lg:max-w-full' }
    },
    height: {
      auto: { content: 'h-auto' },
      fill: { content: 'h-auto max-h-full' }
    }
  },
  defaultVariants: {
    width: 'medium',
    height: 'auto'
  }
})

export interface ModalProps {
  id: string
  open: boolean
  setOpen: (open: boolean) => void
  width?: 'small' | 'medium'
  title?: React.ReactNode
  headerActions?: React.ReactNode
  headerDivider?: boolean
  /** Cap the modal at the viewport height and let the body scroll internally instead of growing the page. */
  fillHeight?: boolean
  children?: React.ReactNode
  onClose?: () => void | Promise<void>
  warningBanner?: boolean
  warningBannerText?: string
  removePadding?: boolean
  zIndex?: number
  backAction?: () => void
  showCross?: boolean
  shouldBlockNavigation?: boolean
  confirmMessage?: string
  style?: React.CSSProperties
}

const Modal: React.FC<ModalProps> = ({
  id,
  open,
  setOpen,
  width = 'medium',
  children,
  onClose,
  title,
  headerActions,
  headerDivider = false,
  fillHeight = false,
  warningBanner,
  warningBannerText,
  removePadding,
  zIndex,
  backAction,
  showCross = true,
  shouldBlockNavigation = false,
  confirmMessage = 'There are unsaved changes - are you sure you wish to continue?',
  style = {}
}) => {
  const { wrapper, content } = modalStyles({ width, height: fillHeight ? 'fill' : 'auto' })
  const appRootElement = document.getElementById('root')
  const modalRef = useRef<HTMLDivElement>(null)
  const shouldBlockNavigationRef = useRef(shouldBlockNavigation)
  const onCloseRef = useRef(onClose)
  const confirmMessageRef = useRef(confirmMessage)
  const blocker = useBlocker(shouldBlockNavigation)

  // Keep the refs in sync with the props (layout effect so they're up-to-date before any browser events)
  useLayoutEffect(() => {
    shouldBlockNavigationRef.current = shouldBlockNavigation
    onCloseRef.current = onClose
    confirmMessageRef.current = confirmMessage
  }, [shouldBlockNavigation, onClose, confirmMessage])

  const handleModalClose = async () => {
    if (open) {
      if (shouldBlockNavigationRef.current) {
        const shouldLeave = window.confirm(confirmMessageRef.current)
        if (!shouldLeave) return
      }
      if (onCloseRef.current) {
        try {
          await onCloseRef.current()
        } catch (error) {
          // pass
        }
      }
      setOpen(false)
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === KEY_PRESS.esc) {
      handleModalClose()
    }
  }

  const handleClick = (event: MouseEvent) => {
    if (modalRef.current) {
      const modalContent = modalRef.current
      if (event.target == modalContent) {
        handleModalClose()
      }
    }
  }

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const shouldLeave = window.confirm(confirmMessageRef.current)
      if (shouldLeave) {
        const proceed = async () => {
          if (onCloseRef.current) {
            try {
              await onCloseRef.current()
            } catch {
              // pass
            }
          }
          setOpen(false)
          blocker.proceed()
        }
        proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker])

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (shouldBlockNavigation) {
      event.preventDefault()
    }
  }

  useBeforeUnload(handleBeforeUnload)

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClick)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [])

  if (appRootElement && open) {
    return ReactDOM.createPortal(
      <div
        id={id}
        className="fixed inset-0 overflow-auto bg-surface-strong/40 select-none"
        style={{ zIndex: zIndex === undefined ? 1000 : zIndex, ...style }}
        onContextMenu={(event) => event.stopPropagation()}
      >
        <div ref={modalRef} className={wrapper()}>
          <div className={content()}>
            <div className={`flex flex-1 flex-col ${fillHeight ? 'min-h-0' : ''}`}>
              {(title || headerActions) && (
                <div className="flex text-lg font-bold items-center min-h-[66px] max-h-[66px] py-4 px-10">
                  {backAction && (
                    <Button onClick={backAction} internalType="transparent" className="mr-2">
                      <Arrow type="left" className="h-5 w-5" />
                    </Button>
                  )}
                  <span className="truncate">{title}</span>
                  {headerActions && <div className="ml-auto mr-8 flex items-center">{headerActions}</div>}
                </div>
              )}
              {headerDivider && (title || headerActions) && <div className="border-b border-divider" />}
              {warningBanner && (
                <div className="w-full flex items-center font-semibold h-[50px] bg-really-light-yellow py-[30px] px-10">
                  <span className="text-lg mr-2.5 text-orange">⚠</span>
                  {warningBannerText ? warningBannerText : "Unexpected bad things will happen if you don't read this!"}
                </div>
              )}
              <div
                className={`flex flex-col ${removePadding ? 'p-0' : 'py-[30px] px-10'} ${
                  fillHeight ? 'flex-1 min-h-0 overflow-hidden' : ''
                }`}
              >
                {children}
              </div>
            </div>
            {showCross && (
              <div
                className="absolute rounded font-bold cursor-pointer hover:bg-surface-sunken top-0 right-0 z-[1] my-5 mx-10"
                onClick={() => handleModalClose()}
              >
                <Cross className="h-5 w-5" />
              </div>
            )}
          </div>
        </div>
      </div>,
      appRootElement
    )
  } else {
    return <div></div>
  }
}

export default Modal
